import * as React from 'react';
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Table, TableCell, TableContainer, TableRow, TableHead, TableBody, FormControl } from '@mui/material';
import Box from '@mui/material/Box';
import { useState, useEffect } from 'react';
import PageInfo from 'components/PageInfo';
import queryString from 'query-string';
import { getFileManagersByQuery } from 'components/axios/fileManagerAxios';
import { getEquipmentCategoriesByQuery } from 'components/axios/equipmentCategoryAxios';
import CustomSelect from 'components/select/CustomSelect';
import { BootstrapInput, fileAccepts, ROLE } from 'data/data';
import { useEnumApi } from 'contexts/EnumApiContext';
import CustomEnumSelect from 'components/select/CustomEnumSelect';
import { fromServerFormat } from 'components/date_picker/dateUtils';
import useConfig from 'hooks/useConfig';
import usePermission from 'hooks/usePermission';
import { PERMISSIONS } from 'config/permissions';

const DataSetting = () => {
    const { getEnumValueByEnumNameAndCode, equipmentCategoryLevel1s, loginInfo } = useEnumApi();
    const { t } = useTranslation();
    const { mode } = useConfig();

    const [searchField, setSearchField] = useState('title');
    const [searchKeyword, setSearchKeyword] = useState('');
    let [pageInfo, setPageInfo] = useState({});
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(process.env.REACT_APP_DEFAULT_PAGE_SIZE);
    const searchFields = [{code:"title", title:t('message.common.subtitle.title')}];

    const [equipmentCategoryLevel2s, setEquipmentCategoryLevel2s] = useState([]);
    const [equipmentCategoryLevel1Id, setEquipmentCategoryLevel1Id] = useState('');
    const [equipmentCategoryLevel2Id, setEquipmentCategoryLevel2Id] = useState('');
    const [datas, setDatas] = useState([]);

    const [isLoading, setIsLoading] = useState(false);
    const [isParam, setIsParam] = useState(false);

    let qs = queryString.parse(window.location.search);
    
    const handlePaging = (value) => {
      value = isNaN(value) ? 1 : value;
      setCurrentPage(value-1);
      setIsLoading(false);
    }
  
    useEffect(()=> {
      if (qs) {
        if (qs.page && qs.page !== (currentPage)) {setCurrentPage(qs.page);}
        if (qs.size) setPageSize(qs.size);
        if (qs.searchField) setSearchField(qs.searchField);
        if (qs.searchKeyword) setSearchKeyword(qs.searchKeyword);

        if (qs.searchlevel1) setEquipmentCategoryLevel1Id(qs.searchlevel1);
        if (qs.searchlevel2) setEquipmentCategoryLevel2Id(qs.searchlevel2);
      }
      setIsParam(true);
    }, [qs.page, qs.size, qs.searchField, qs.searchKeyword]);

    // 설비중분류 목록
    useEffect(()=>{
        if (equipmentCategoryLevel1Id) {
            const param = { "parent.id": equipmentCategoryLevel1Id, depth: 2, size : 100, sort : "name,asc" };
            getEquipmentCategoriesByQuery(param).then((data) => {
                if (data && data._embedded) {
                    const list = data._embedded.equipmentCategories;
                    if (list) {
                        setEquipmentCategoryLevel2s(list);
                    } else {
                        setEquipmentCategoryLevel2s([]);
                    }
                } else {
                    setEquipmentCategoryLevel2s([]);
                }
            });
        }
    },[equipmentCategoryLevel1Id]);
  
    useEffect(()=> {
      if (!isLoading && !isNaN(currentPage) && isParam) {
        dataList();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, isLoading, isParam]);
  
    const dataList = async(curPage=currentPage) => {
      if (isNaN(curPage)) {setCurrentPage(0)};
      if(isNaN(pageSize)) {setPageSize(process.env.REACT_APP_DEFAULT_PAGE_SIZE);}
      setCurrentPage(curPage)
  
      let param = {
        page : curPage,
        size : pageSize,
        fileManagerType : "C"
      };
      if (equipmentCategoryLevel1Id)
        param["equipmentCtgr.parent.id"] = equipmentCategoryLevel1Id;
      if (equipmentCategoryLevel2Id)
        param["equipmentCtgr.id"] = equipmentCategoryLevel2Id;

      let keyword = searchKeyword;
      if (searchKeyword) {
        keyword = 'contains('+searchKeyword+')';
        param.title = keyword;
      }
      await getFileManagersByQuery(param).then((data)=>{
        if (data != null) {
          setPageInfo(data.page);
          if(data._embedded) {
            setDatas(data._embedded.fileManagers);
          } else {
            setCurrentPage(0);
            setDatas([]);
          }
        } else {
          pageInfo = {
            size : pageInfo.size,
            totalElements : 0,
            totalPages : 0,
            number : 0
          }
          setPageInfo(pageInfo);
          setDatas([]);
        }
      });
      setIsLoading(true);
    }
   
    const handleSelect = (event, value) => {
      event.preventDefault();
      setSearchField(value.props.value);
    };
  
    const handleInput = (event) => {
      event.preventDefault();
      setSearchKeyword(event.target.value.trim());
    };
  
    const handleOnKeyPress = e => {
      if (e.key === 'Enter') {
        handleSearch(e); // Enter 입력이 되면 클릭 이벤트 실행
      }
    };
  
    const handleSearch = (event) => {
      event.preventDefault();
      setCurrentPage(0);
      pageInfo.number = 0;
      setPageInfo(pageInfo);
      dataList(0);
    };

    const searchParam = '?searchField='+searchField+'&searchKeyword='+searchKeyword+'&page='+currentPage+'&size='+pageSize+"&searchlevel1="+equipmentCategoryLevel1Id+"&searchlevel2="+equipmentCategoryLevel2Id;

    const { hasPermission } = usePermission();
    const equipmentCategorySearchPermission = hasPermission(PERMISSIONS.EQUIPMENT_CATEGORY_SEARCH);
    const canWrite  = hasPermission(PERMISSIONS.SETTING_DATAROOM_WRITE);

    return (
      <div className='cal' style={{padding:"15px 0"}}>
        <div className='search_section'>
          <div style={{display:"flex",justifyContent:"space-between"}}>
            <Box sx={{ minWidth: 120 }}>
            {isLoading ? 
            <>
            {equipmentCategorySearchPermission && equipmentCategoryLevel1s && 
              <FormControl sx={{ m: 1, minWidth: 80 }}>
                  <CustomSelect
                  key={equipmentCategoryLevel1Id}
                  label={t('message.common.subtitle.equipment.level1')}
                  labelName={t('message.common.subtitle.equipment.level1')}
                  name={'level1'}
                  defaultValue={equipmentCategoryLevel1Id}
                  onChange={(e)=>{
                      setEquipmentCategoryLevel1Id(e.target.value);
                      setEquipmentCategoryLevel2Id('');
                  }}
                  list={equipmentCategoryLevel1s}
              />
              </FormControl>
              }
              {equipmentCategorySearchPermission && equipmentCategoryLevel2s && 
              <FormControl sx={{ m: 1, minWidth: 80 }}>
                  <CustomSelect
                  key={equipmentCategoryLevel2Id}
                  label={t('message.common.subtitle.equipment.level2')}
                  labelName={t('message.common.subtitle.equipment.level2')}
                  name={'level2'}
                  defaultValue={equipmentCategoryLevel2Id}
                  onChange={(e)=>{
                      setEquipmentCategoryLevel2Id(e.target.value);
                  }}
                  list={equipmentCategoryLevel2s}
              />
              </FormControl>
              }
              <FormControl sx={{ m: 1, minWidth: 120 }}>
                  <CustomEnumSelect
                      key={searchField}
                      label={t('message.common.btn.search')}
                      labelName={t('message.common.btn.search')}
                      name={'searchField'}
                      defaultValue={searchField}
                      onChange={(e)=>{
                          setSearchField(e.target.value);
                      }}
                      isLabelView={false}
                      list={searchFields}
                  />
              </FormControl>
  
              <FormControl sx={{ m: 1 }} variant="standard">
                  <BootstrapInput id="demo-customized-textbox" value={searchKeyword} onChange={handleInput} onKeyPress={handleOnKeyPress} />
              </FormControl>
  
              <Button variant="contained" style={{marginTop:"7px",padding:"7px 15px"}} onClick={handleSearch}>
                <span className="icon_png"><img src={`${process.env.PUBLIC_URL}/img/icon/search.png`} alt='' /></span>{t('message.common.btn.search')}
              </Button>
              </>
              :""}
            </Box>
            {canWrite && 
            <div className="btn_right">
              <Link to={'./add'+searchParam}><Button variant="contained" color="primary">{t('message.common.btn.write')}</Button></Link>
            </div>
            }
          </div>
        </div>

        <Box 
          id="tableDiv"
          sx={{
            mt: 3,
            padding: "20px",
            borderRadius: "20px",
            overflow: "hidden",
            background: `${mode === 'dark' ? "#182131": "#fff"}`,
            border: `${mode === 'dark' ? "1px solid rgba(255,255,255,0.06)": "1px solid #30363d"}`,
            boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
          }}
        >
          <TableContainer>
            <Table className="list_table" sx={{ minWidth: 900 }} aria-label="simple table" style={{borderTop:"2px solid #a2a2a2"}}>
              <TableHead>
                <TableRow>
                  <TableCell className="th" align="center">No.</TableCell>
                  <TableCell className="th" align="center">{t('message.common.subtitle.equipment.level1')}</TableCell>
                  <TableCell className="th" align="center">{t('message.common.subtitle.equipment.level2')}</TableCell>
                  <TableCell className="th" align="center">{t('message.common.select.division')}</TableCell>
                  <TableCell className="th" align="center">{t('message.common.subtitle.type2')}</TableCell>
                  <TableCell className="th" align="center">{t('message.common.subtitle.title')}</TableCell>
                  <TableCell className="th" align="center">{t('message.common.subtitle.createdate')}</TableCell>
                </TableRow>
              </TableHead>
    
              <TableBody>
              { datas && datas.map(function(item, i) {
                let linkUrl = './'+item.id+searchParam;
                let accept = null;
                if (item.attachedFiles && item.attachedFiles.length > 0) {
                    const extension = item.attachedFiles[0].originFile.substring(item.attachedFiles[0].originFile.lastIndexOf("."));
                    accept = fileAccepts.filter(acc => acc.extension.indexOf(extension.toLowerCase()) !== -1);
                }
                return(
                <TableRow key={i}>
                  <TableCell align="center"><Link to={linkUrl}>{pageInfo.totalElements-((currentPage)*pageInfo.size)-i}</Link></TableCell>
                  <TableCell align="center"><Link to={linkUrl}>{item.equipmentCtgr && item.equipmentCtgr.parent && item.equipmentCtgr.parent.name}</Link></TableCell>
                  <TableCell align="center"><Link to={linkUrl}>{item.equipmentCtgr && item.equipmentCtgr.name}</Link></TableCell>
                  <TableCell align="center"><Link to={linkUrl}>{item.fileType && getEnumValueByEnumNameAndCode("FileType", item.fileType)?.[0]?.title}</Link></TableCell>
                  <TableCell align="center"><Link to={linkUrl}>{accept && (t(accept[0].titleKey) || '')}</Link></TableCell>
                  <TableCell align="center"><Link to={linkUrl}>{item.title}</Link></TableCell>
                  <TableCell align="center"><Link to={linkUrl}>{fromServerFormat(item.createdAt, {format:t('message.common.text.date.format.date')})}</Link></TableCell>
                </TableRow>
                )
              }) }
              </TableBody>
            </Table>
          </TableContainer>
    
          {canWrite && 
          <div className="btn_right">
            <Link to={'./add'+searchParam}><Button variant="contained" color="primary">{t('message.common.btn.write')}</Button></Link>
          </div>
          }
    
          <PageInfo pageInfo={pageInfo} handlePaging={handlePaging}/>
        </Box>
      </div>
    );
}

export default DataSetting;